import { type DashboardOverview } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';

import { Card } from '../../components/Card';
import { DonutShare, LineTrend } from '../../components/charts';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { Pill } from '../../components/Pill';
import { dashboardApi } from '../../lib/apiClient';
import { bangkokBusinessDay } from '../../lib/businessDate';
import { CHART_H } from '../../lib/chartTokens';
import { dayToIso } from '../../lib/dateRange';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtInt,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

const STATUS_TONE = {
  online: 'ok',
  syncing: 'primary',
  offline: 'danger',
  unknown: 'neutral',
} as const;
const STATUS_TH = {
  online: 'ออนไลน์',
  syncing: 'กำลังซิงค์',
  offline: 'ออฟไลน์',
  unknown: 'ไม่ทราบ',
} as const;

const ALERT_CLASSES: Record<
  DashboardOverview['alerts'][number]['severity'],
  string
> = {
  danger: 'bg-danger-light/30 border-danger/15 hover:bg-danger-light/50',
  warn: 'bg-warn-light/30 border-warn/15 hover:bg-warn-light/50',
  info: 'bg-primary-light/30 border-primary/15 hover:bg-primary-light/50',
};

const ALERT_ICON_CLASSES: Record<
  DashboardOverview['alerts'][number]['severity'],
  string
> = {
  danger: 'bg-danger text-white',
  warn: 'bg-warn text-white',
  info: 'bg-primary text-white',
};

// Custom inline SVG icons for KPI Cards to prevent external package dependencies
const RevIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const BillIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
    <path d="M6 8h12M6 12h12M6 16h12" />
  </svg>
);

const AvgIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const PayIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

export function DashboardView() {
  const { scope, ready } = useBranchScope();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', scope.branchId, scope.allowedBranchIds],
    queryFn: () =>
      dashboardApi.getOverview({
        date: dayToIso(bangkokBusinessDay()),
        branchId: scope.branchId ?? undefined,
      }),
    enabled: ready,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: () => dashboardApi.listBranches(),
  });
  const shortOf = (id: string) =>
    branches.find((b) => b.id === id)?.short ?? id;

  if (!ready || isLoading) {
    return (
      <div>
        <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bo-card h-[120px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />
        <div className="bo-card p-8 text-center space-y-4">
          <div className="font-bold text-danger">โหลดข้อมูลภาพรวมไม่สำเร็จ</div>
          <button
            className="bo-btn bo-btn-primary"
            onClick={() => void refetch()}
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  const {
    kpis,
    branches: branchToday,
    topProducts,
    recentReceipts,
    alerts,
  } = data;
  const chartSkeleton = (
    <div
      className="animate-pulse bg-bg-warm rounded-card"
      style={{ height: CHART_H.md }}
    />
  );
  const trend = data.trend.map((row) => ({
    label: row.date.slice(-2),
    value: row.amount,
  }));
  const paySplit = data.payments.map((row) => ({
    label: row.label,
    value: row.amount,
  }));
  return (
    <div className="bo-fade space-y-6">
      <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="ยอดขายวันนี้"
          value={fmtAmount(kpis.revenue)}
          unit="บาท"
          delta={kpis.growth ?? undefined}
          sub="เทียบกับเมื่อวันก่อน"
          icon={<RevIcon />}
          tone="primary"
        />
        <KpiCard
          label="จำนวนบิล"
          value={fmtInt(kpis.bills)}
          unit="บิล"
          sub="ทำรายการสำเร็จวันนี้"
          icon={<BillIcon />}
          tone="accent"
        />
        <KpiCard
          label="ยอดเฉลี่ย/บิล"
          value={fmtAmount(kpis.averageBill)}
          unit="บาท"
          sub="มูลค่าเฉลี่ยต่อตะกร้าสินค้า"
          icon={<AvgIcon />}
          tone="ok"
        />
        <KpiCard
          label="สัดส่วนชำระ (เงินสด/QR)"
          value={
            kpis.cashShare === null || kpis.qrShare === null
              ? 'ยังไม่มีข้อมูล'
              : `${fmtPct(kpis.cashShare * 100)} / ${fmtPct(kpis.qrShare * 100)}`
          }
          sub="ช่องทางชำระเงินวันนี้"
          icon={<PayIcon />}
          tone="warn"
        />
      </div>

      {/* Trend + payment split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="แนวโน้มยอดขาย 14 วัน" className="lg:col-span-2 shadow-sm">
          <Suspense fallback={chartSkeleton}>
            <LineTrend
              data={trend}
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
            />
          </Suspense>
        </Card>
        <Card title="สัดส่วนการชำระเงิน (บาท)" className="shadow-sm">
          <Suspense fallback={chartSkeleton}>
            <DonutShare data={paySplit} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>
      </div>

      {/* Main Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Branch Sales Performance Card */}
        <Card title="ยอดขายแต่ละสาขา" className="lg:col-span-2 shadow-sm">
          <div className="overflow-x-auto">
            <table className="bo-table">
              <thead>
                <tr>
                  <th className="py-3 px-4 font-semibold text-ink-3">สาขา</th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    ยอดขาย
                  </th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    จำนวนบิล
                  </th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    Δ เทียบวันก่อน
                  </th>
                  <th className="py-3 px-4 font-semibold text-ink-3">
                    สถานะเชื่อมต่อ
                  </th>
                </tr>
              </thead>
              <tbody>
                {branchToday.map((b) => (
                  <tr
                    key={b.branchId}
                    className="hover:bg-bg-warm/40 transition-colors cursor-default"
                  >
                    <td className="py-3 px-4 text-ink font-semibold">
                      {shortOf(b.branchId)}
                    </td>
                    <td className="bo-td-num py-3 px-4 num font-bold text-ink">
                      {fmtAmount(b.revenue)}
                    </td>
                    <td className="bo-td-num py-3 px-4 num text-ink-2">
                      {fmtInt(b.bills)}
                    </td>
                    <td
                      className={`bo-td-num py-3 px-4 num font-bold ${(b.growth ?? 0) > 0 ? 'text-ok' : (b.growth ?? 0) < 0 ? 'text-danger' : 'text-ink-4'}`}
                    >
                      {b.growth === null
                        ? '—'
                        : `${b.growth > 0 ? '▲ +' : b.growth < 0 ? '▼ ' : '– '}${Math.abs(b.growth * 100).toFixed(1)}%`}
                    </td>
                    <td className="py-3 px-4">
                      <Pill tone={STATUS_TONE[b.connectivity]}>
                        {STATUS_TH[b.connectivity]}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Notifications & Alerts */}
        <Card title="แจ้งเตือน & ความปลอดภัย" className="shadow-sm">
          <ul className="flex flex-col gap-3">
            {alerts.length === 0 ? (
              <div className="text-center py-6 text-sm text-ink-4">
                ไม่มีรายการแจ้งเตือนในขณะนี้
              </div>
            ) : (
              alerts.map((a, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-3 p-3 rounded-btn border ${ALERT_CLASSES[a.severity]} transition-all duration-200 shadow-sm`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-sm ${ALERT_ICON_CLASSES[a.severity]}`}
                  >
                    !
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-body font-bold text-ink leading-snug">
                      {a.title}
                    </div>
                    <div className="text-[11px] text-ink-2 leading-relaxed">
                      {a.detail}
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </Card>
      </div>

      {/* Grid: Top Sellers & Recent Bills */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Sellers Table */}
        <Card title="สินค้าขายดีวันนี้" className="shadow-sm">
          <div className="overflow-x-auto">
            <table className="bo-table">
              <thead>
                <tr>
                  <th className="py-3 px-4 font-semibold text-ink-3">สินค้า</th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    จำนวนชิ้น
                  </th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    ยอดขายรวม
                  </th>
                </tr>
              </thead>
              <tbody>
                {topProducts.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-ink-4">
                      ยังไม่มีข้อมูล
                    </td>
                  </tr>
                ) : (
                  topProducts.map((t) => (
                    <tr
                      key={t.productId}
                      className="hover:bg-bg-warm/40 transition-colors cursor-default"
                    >
                      <td className="py-3 px-4 text-ink font-medium">
                        {t.productName}
                      </td>
                      <td className="bo-td-num py-3 px-4 num font-bold text-ink-2">
                        {fmtInt(t.quantity)}
                      </td>
                      <td className="bo-td-num py-3 px-4 num font-bold text-ink">
                        {fmtAmount(t.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent Bills Table */}
        <Card title="บิลล่าสุด" className="shadow-sm">
          <div className="overflow-x-auto">
            <table className="bo-table">
              <thead>
                <tr>
                  <th className="py-3 px-4 font-semibold text-ink-3">เลขบิล</th>
                  <th className="py-3 px-4 font-semibold text-ink-3">
                    วิธีชำระเงิน
                  </th>
                  <th className="bo-th-num py-3 px-4 font-semibold text-ink-3">
                    ยอดชำระ
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentReceipts.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-ink-4">
                      ยังไม่มีข้อมูล
                    </td>
                  </tr>
                ) : (
                  recentReceipts.map((t) => (
                    <tr
                      key={t.saleId}
                      className="hover:bg-bg-warm/40 transition-colors cursor-default"
                    >
                      <td className="py-3 px-4 text-ink font-medium num">
                        {t.receiptNo}
                      </td>
                      <td className="py-3 px-4">
                        <Pill
                          tone={
                            t.paymentMethods.includes('cash') ? 'accent' : 'qr'
                          }
                        >
                          {t.paymentMethods.includes('cash')
                            ? 'เงินสด'
                            : 'QR Code'}
                        </Pill>
                      </td>
                      <td className="bo-td-num py-3 px-4 num font-bold text-primary">
                        {fmtBaht(t.totalAmount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
